import {
	ref,
	Ref,
	nextTick,
	watchEffect,
	onUnmounted,
} from '@vue/composition-api';
import { STANDARD_CONTRAST_RATIO } from '@front-app/util/color';
import { MaybeElementRef, unrefElement } from '@vueuse/core';
import { useDebounceFn } from '@vueuse/shared';
import getContrast from 'get-contrast';

const ATTRIBUTE_NAMES_TO_WATCH = ['color', 'background-color', 'background'];

interface UseContrast {
	elRef: MaybeElementRef;
	test(): void;
	contrastIsAcceptable: Ref<boolean>;
}

export function useContrast(): UseContrast {
	const element: MaybeElementRef = ref(null);
	const contrastIsAcceptable = ref(true);

	const testContrast = async () => {
		await nextTick();
		const elVal = unrefElement(element);
		if (!elVal) {
			contrastIsAcceptable.value = true;
			return;
		}
		const { color, backgroundColor } = window.getComputedStyle(elVal);
		contrastIsAcceptable.value = getContrast.ratio(color, backgroundColor) >= STANDARD_CONTRAST_RATIO;
	};
	const debouncedTestContrast = useDebounceFn(testContrast, 100);

	const mutationHandler = (mutations: MutationRecord[]) => {
		mutations.filter(({ type }) => type === 'attributes')
			.flatMap((rec) => (rec.attributeName ? [rec.attributeName] : []))
			.some((attr) => ATTRIBUTE_NAMES_TO_WATCH.includes(attr));
	};

	let observer: MutationObserver | undefined;
	const removeObserver = () => {
		if (observer) {
			observer.disconnect();
		}
	};
	const setUpObserver = () => {
		const el = unrefElement(element);
		if (!window.MutationObserver || !el) {
			return;
		}
		removeObserver();

		observer = new MutationObserver(mutationHandler);
		observer.observe(el, { attributes: true });
	};

	onUnmounted(removeObserver);

	watchEffect(setUpObserver, { flush: 'post' });

	return {
		elRef: element,
		test: debouncedTestContrast,
		contrastIsAcceptable,
	};
}
